
import React, { useState, useEffect, useRef } from 'react';
import { Plus, MapPin } from 'lucide-react';
import AddItemModal from '../../components/AddItemModal';
import AddressManager from '../../components/AddressManager';
import DealerLocationLinksManager from '../../components/DealerLocationLinksManager';
import DealerLocationFactFindManager from '../../components/DealerLocationFactFindManager';
import { Link } from 'react-router-dom';
import {
    fetchDealerLocationsWithAddresses,
    updateDealerLocationAddress
} from '../../services/apiService';

const LocationConfigurationPage = ({ dealership, factfindTemplate, linksTemplate, onUpdateDealership }) => {
    const [currentLocationId, setCurrentLocationId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [configTab, setConfigTab] = useState('factfind');
    const [factfindSubTab, setFactfindSubTab] = useState('sales');
    const [locationsWithAddresses, setLocationsWithAddresses] = useState([]);
    const [showAddressSelector, setShowAddressSelector] = useState(false);
    const linksManagerRef = useRef();
    const factfindManagerRef = useRef();
    const addressManagerRef = useRef();

    const handleDataChange = (locationId, dataType, fieldId, value) => {
        onUpdateDealership({ ...dealership, locations: dealership.locations.map(loc => loc.id === locationId ? { ...loc, [dataType]: { ...loc[dataType], [fieldId]: value } } : loc) });
    };

    useEffect(() => {
        if (dealership?.id) {
            loadLocationsWithAddresses();
        }
    }, [dealership?.id]);

    // Add page leave validation
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            let isValid = true;

            if (configTab === 'links' && linksManagerRef.current) {
                isValid = linksManagerRef.current.validateRequiredFields() && isValid;
            }

            if (configTab === 'factfind' && factfindManagerRef.current) {
                isValid = factfindManagerRef.current.validateRequiredFields() && isValid;
            }

            if (!isValid) {
                event.preventDefault();
                event.returnValue = 'You have unfilled required fields. Are you sure you want to leave?';
                return event.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [configTab]);

    const loadLocationsWithAddresses = async () => {
        try {
            const locations = await fetchDealerLocationsWithAddresses();
            // Filter locations to only those belonging to the current dealership's group
            const filteredLocations = locations.filter(loc => loc.group_id === dealership.id);
            setLocationsWithAddresses(filteredLocations);
            
            // If no location is selected, select the first one
            if (!currentLocationId && filteredLocations.length > 0) {
                setCurrentLocationId(filteredLocations[0].id);
            }
        } catch (error) {
            console.error('Error loading locations with addresses:', error);
        }
    };

    const handleTabChange = (newTab) => {
        let isValid = true;

        // Check current tab validation before switching
        if (configTab === 'links' && linksManagerRef.current) {
            isValid = linksManagerRef.current.validateRequiredFields() && isValid;
        }

        if (configTab === 'factfind' && factfindManagerRef.current) {
            isValid = factfindManagerRef.current.validateRequiredFields() && isValid;
        }

        if (!isValid) {
            const confirmLeave = window.confirm('You have unfilled required fields. Are you sure you want to leave this tab?');
            if (!confirmLeave) {
                return; // Don't change tabs
            }
        }

        setConfigTab(newTab);
    };

    const handleAddLocation = (formData) => {
        const newLocation = { id: `loc-${Date.now()}`, name: formData.locationName, factfindData: {}, linksData: {} };
        onUpdateDealership({ ...dealership, locations: [...(dealership.locations || []), newLocation] });
        setCurrentLocationId(newLocation.id);
    };

    const handleAddressSelect = async (address) => {
        if (currentLocationId) {
            try {
                await updateDealerLocationAddress(currentLocationId, address.id);
                await loadLocationsWithAddresses(); // Reload to get updated data
                setShowAddressSelector(false);
            } catch (error) {
                console.error('Error updating location address:', error);
                alert('Failed to update location address');
            }
        }
    };

    const handleAddressUpdate = async (address) => {
        // When an address is updated, if it's the current location's address, update it
        if (currentLocationId && currentLocationWithAddress?.address_id === address.id) {
            try {
                await loadLocationsWithAddresses(); // Reload to get updated data
            } catch (error) {
                console.error('Error reloading locations:', error);
            }
        }
    };

    const currentLocationWithAddress = locationsWithAddresses.find(loc => loc.id === currentLocationId);

    return (
        <div>
            <div className="mb-6">
                <Link to={`/setup/${dealership.id}`} className="text-sm text-blue-600 hover:underline">
                    &larr; Back to {dealership.name}
                </Link>
            </div>
            <h2 className="text-3xl font-bold mb-4">{dealership.name} - Location Configuration</h2>
            <div className="flex items-start gap-8">
                    <div className="w-1/4">
                    <div className="bg-white p-4 rounded-lg shadow-md border">
                        <h3 className="font-bold mb-3">Locations</h3>
                        <div className="flex flex-col gap-2 max-h-96 overflow-y-auto pr-2">
                            {locationsWithAddresses.length > 0 ? (
                                locationsWithAddresses.map(loc => (
                                    <button
                                        key={loc.id}
                                        onClick={() => setCurrentLocationId(loc.id)}
                                        className={`w-full text-left px-3 py-2 text-sm rounded-md ${currentLocationId === loc.id ? 'bg-blue-100 font-semibold' : 'hover:bg-gray-100'}`}>
                                        {loc.name}
                                    </button>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500">No locations found</p>
                            )}
                        </div>
                        <button onClick={() => setIsModalOpen(true)} className="mt-4 w-full text-sm flex items-center justify-center gap-2 p-2 bg-gray-100">
                            <Plus size={16} /> Add Location
                        </button>
                    </div>
                </div>
                <div className="w-3/4">
                    {currentLocationWithAddress ? (
                        <div className="bg-white rounded-xl shadow-md border">
                            <div className="p-6 border-b">
                                <h3 className="text-xl font-bold">{currentLocationWithAddress.name}</h3>
                                <div className="mt-4 flex border-b">
                                    <button onClick={() => handleTabChange('factfind')} className={`px-4 py-2 text-sm ${configTab === 'factfind' ? 'border-b-2 border-blue-600' : ''}`}>Fact Find</button>
                                    <button onClick={() => handleTabChange('links')} className={`px-4 py-2 text-sm ${configTab === 'links' ? 'border-b-2 border-blue-600' : ''}`}>Links</button>
                                    <button onClick={() => handleTabChange('address')} className={`px-4 py-2 text-sm ${configTab === 'address' ? 'border-b-2 border-blue-600' : ''}`}>Address</button>
                                </div>
                            </div>
                            <div className="p-6">
                                {configTab === 'factfind' && currentLocationId && (
                                    <DealerLocationFactFindManager
                                        ref={factfindManagerRef}
                                        locationId={currentLocationId}
                                        factfindTemplate={factfindTemplate}
                                    />
                                )}
                                {configTab === 'links' && currentLocationId && (
                                    <DealerLocationLinksManager
                                        ref={linksManagerRef}
                                        locationId={currentLocationId}
                                        linksTemplate={linksTemplate}
                                    />
                                )}
                                {configTab === 'address' && (
                                    <div>
                                        <div className="mb-4">
                                            <div className="flex items-center justify-between mb-4">
                                                <h4 className="text-lg font-semibold flex items-center gap-2">
                                                    <MapPin size={20} />
                                                    Location Address
                                                </h4>
                                                <button
                                                    onClick={() => setShowAddressSelector(!showAddressSelector)}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
                                                >
                                                    {showAddressSelector ? 'Cancel' : 'Change Address'}
                                                </button>
                                            </div>

                                            {currentLocationWithAddress?.address_id ? (
                                                <div className="p-4 bg-gray-50 rounded-lg">
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            <div className="font-medium">
                                                                {[
                                                                    currentLocationWithAddress.building_name,
                                                                    currentLocationWithAddress.building_number,
                                                                    currentLocationWithAddress.street_name,
                                                                    currentLocationWithAddress.locality,
                                                                    currentLocationWithAddress.town_city,
                                                                    currentLocationWithAddress.county,
                                                                    currentLocationWithAddress.postcode
                                                                ].filter(Boolean).join(', ')}
                                                            </div>
                                                            <div className="text-sm text-gray-600 mt-1">
                                                                {currentLocationWithAddress.country} • {currentLocationWithAddress.address_type}
                                                            </div>
                                                        </div>
                                                        {!showAddressSelector && currentLocationWithAddress.address_id && (
                                                            <button
                                                                onClick={() => setShowAddressSelector(true)}
                                                                className="ml-4 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                                            >
                                                                Edit Address
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                                    <div className="text-yellow-800">
                                                        No address assigned to this location. Click "Change Address" to assign one.
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {showAddressSelector && (
                                            <div className="border-t pt-4">
                                                <AddressManager
                                                    ref={addressManagerRef}
                                                    showSelector={true}
                                                    selectedAddressId={currentLocationWithAddress?.address_id}
                                                    onAddressSelect={handleAddressSelect}
                                                    onAddressUpdate={handleAddressUpdate}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white p-8 rounded-xl shadow-md border text-center">
                            <p>No location selected.</p>
                            <p className="text-sm text-gray-500">Please select a location or add a new one.</p>
                        </div>
                    )}
                </div>
            </div>
            <AddItemModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleAddLocation}
                title="Add New Location"
                fields={[{ name: 'Location Name', id: 'locationName' }]}
            />
        </div>
    );
};

export default LocationConfigurationPage;
